import {
  CacheableEntryJson,
  EntryDataType,
  EntryIndexType,
} from 'types/helpers'

import {
  RolodexEntryJson,
  RolodexEntryTagsType,
  RolodexEntryType,
} from './rolodexEntryFields'

/**
 * The base interface from which all Gecko<NAME> objects inherit (i.e. GeckoJob)
 */
export interface GeckoEntry {
  /** The unique ID for this entry */
  readonly id: string

  /** The  entry "type", similar to a "class" or "kind" */
  readonly type: RolodexEntryType

  /** Data associated with this  entry */
  readonly data: EntryDataType

  /** A record of IDs per type associated to this entry */
  readonly tags: RolodexEntryTagsType

  /** An index used for efficient file lookup */
  readonly index: EntryIndexType

  /** A numeric identifier tracking updates to an entry or its files */
  readonly generation: number

  /** A string field that represents when the entry was created */
  readonly created_at?: string

  /** A string field that represents when the entry was last updated */
  readonly updated_at: string

  // TODO PAC: reconsider this
  readonly raw: RolodexEntryJson

  /** Whether this entry is from the local cache */
  readonly isFromCache: boolean

  /** A bool whether the entry is visible to customers, values are "internal" or "none" */
  readonly view_restriction: 'internal' | null
}

/**
 * A templated, abstract base class which "Gecko<NAME>" objects can inherit from
 */
export abstract class GeckoEntryBase<
  DataType extends EntryDataType = EntryDataType,
  TagsType extends RolodexEntryTagsType = RolodexEntryTagsType,
  IndexType extends EntryIndexType = EntryIndexType,
> implements GeckoEntry
{
  //----------------------------------------------------------------------------

  /** The -wide unique ID for this entry */
  public readonly id: string

  /** The  entry "type", similar to a "class" or "kind" */
  public readonly type: RolodexEntryType

  /** Data associated with this  entry */
  public readonly data: DataType & EntryDataType // TODO: remove this loose typing

  /** A record of IDs per type associated to this entry */
  public readonly tags: TagsType

  /** An index used for efficient file lookup */
  public readonly index: IndexType

  /** A numeric identifier tracking updates to an entry or its files */
  public readonly generation: number

  /** A string field that represents when the entry was created */
  readonly created_at?: string

  /** A string field that represents when the entry was last updated */
  public readonly updated_at: string

  /** Whether this entry is from the local cache */
  public readonly isFromCache: boolean

  // TODO PAC: reconsider this
  readonly raw: RolodexEntryJson

  /** A bool whether the entry is visible to customers, values are "internal" or "none" */
  public readonly view_restriction: 'internal' | null

  //----------------------------------------------------------------------------

  /**
   * A protected constructor used by subclasses of this one
   * @param json    The raw JSON to create an instance from
   * @param data    Data to attach to this entry
   * @param tags    Tags used to link this entry to other entries
   * @param index   An optional index used for fast image lookup
   * @protected
   */
  protected constructor(
    json: CacheableEntryJson,
    data: DataType,
    tags: TagsType,
    index: IndexType
  ) {
    this.id = json.id
    this.type = json.type

    // Assume all data from the JSON unless it's overwritten when calling super()
    this.data = { ...json.data, ...data }
    this.tags = { ...json.tags, ...tags }
    this.index = { ...json.index, ...index }
    this.generation = json.generation
    this.created_at = json.created_at
    this.updated_at = json.updated_at

    const { isFromCache, ...raw } = json
    this.raw = { ...raw }
    this.isFromCache = isFromCache ?? false
    this.view_restriction =
      json.view_restriction === 'internal' ? 'internal' : null
  }
}
